/**
 * Gift wrap — <hdt-gift-wrap>
 *
 * Adds the gift-wrap variant alongside the main product in ONE /cart/add.js request,
 * reusing the theme's existing ProductForm submit handler (which builds its body from
 * `new FormData(this.form)`). We only intervene when the control is checked:
 *
 *   1. Listen for `submit` in the CAPTURE phase so we run before the theme's bubble
 *      handler reads FormData.
 *   2. The theme's /cart/add.js call processes BOTH the form's top-level `id`/`quantity`
 *      (the main product) AND an `items[]` array in the same request. So we leave the main
 *      product to the existing `id`/`quantity` and inject ONLY the gift wrap as a single
 *      `items[]` entry — main is added once, wrap once, no double-add. Keeping the `id`
 *      input also satisfies the theme's success handler (it reads `FormData.get("id")` to
 *      fire its CartAddEvent / refresh the drawer, and throws if it's missing).
 *   3. Remove the injected inputs on the next tick so a failed/repeat submit still works.
 *
 * Variant gating: when data-eligible-variants is set, the row only shows (and the wrap is only
 * added / priced in) while the selected main variant is in that list — toggled on `variant:change`.
 *
 * Hooks both the main product form and the sticky add-to-cart form.
 *
 * Dynamic checkout ("Buy it now"): the Shopify payment button (`{{ form | payment_button }}`)
 * checks out ONLY the form's single line item and never hits /cart/add.js, so the items[]
 * injection above can't reach it. When the wrap is checked we intercept the click on the
 * `.shopify-payment-button` container in the CAPTURE phase, block Shopify's express handler,
 * add the main product + wrap via /cart/add.js, then send the buyer to /checkout. (The branded
 * Shop Pay express button renders in a cross-origin iframe whose clicks can't be intercepted —
 * the unbranded "Buy it now" button is the one this covers.)
 */
(function () {
  if (customElements.get('hdt-gift-wrap')) return;

  class GiftWrap extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-gift-wrap-input]');
      this.variantId = this.dataset.giftWrapVariantId;
      if (!this.input || !this.variantId) return;

      this.forms = [this.dataset.form, this.dataset.stickyForm]
        .map((id) => (id ? document.getElementById(id) : null))
        .filter((form, index, all) => form && all.indexOf(form) === index);

      this.onSubmit = this.onSubmit.bind(this);
      this.forms.forEach((form) => form.addEventListener('submit', this.onSubmit, true));

      // Intercept the dynamic checkout ("Buy it now") buttons in each form (capture phase).
      this.onBuyNow = this.onBuyNow.bind(this);
      this.buyNowButtons = [];
      this.forms.forEach((form) => {
        form.querySelectorAll('.shopify-payment-button').forEach((btn) => {
          btn.addEventListener('click', this.onBuyNow, true);
          this.buyNowButtons.push(btn);
        });
      });

      this.setupEligibility();
      this.setupButtonPrice();
    }

    disconnectedCallback() {
      if (this.forms) {
        this.forms.forEach((form) => form.removeEventListener('submit', this.onSubmit, true));
      }
      if (this.buyNowButtons) {
        this.buyNowButtons.forEach((btn) => btn.removeEventListener('click', this.onBuyNow, true));
      }
      if (this.onVariantChange) {
        document.getElementById(this.dataset.form)?.removeEventListener('variant:change', this.onVariantChange);
      }
      if (this.priceTrigger) {
        this.qtyInput?.removeEventListener('change', this.priceTrigger);
        this.qtyInput?.removeEventListener('input', this.priceTrigger);
        this.input.removeEventListener('change', this.priceTrigger);
      }
    }

    /**
     * Variant-level gating. data-eligible-variants is a comma list of main-product variant ids
     * that offer gift wrap (empty => every variant). The row is shown only when the currently
     * selected variant is eligible; ineligible variants never add the wrap or affect the price.
     */
    setupEligibility() {
      this.root = this.closest('.hdt-gift-wrap') || this;
      const raw = (this.dataset.eligibleVariants || '').trim();
      this.eligibleSet = raw ? new Set(raw.split(',').map((s) => s.trim())) : null; // null => all
      this.eligible = true;

      const mainForm = document.getElementById(this.dataset.form);
      const currentId = mainForm ? new FormData(mainForm).get('id') : null;
      this.applyEligibility(currentId);

      // The theme dispatches "variant:change" on the form with detail.variant.
      this.onVariantChange = (event) => {
        const variant = event.detail && event.detail.variant;
        this.applyEligibility(variant ? variant.id : null);
      };
      mainForm?.addEventListener('variant:change', this.onVariantChange);
    }

    applyEligibility(variantId) {
      this.eligible = !this.eligibleSet || (variantId != null && this.eligibleSet.has(String(variantId)));
      this.root.style.display = this.eligible ? '' : 'none';
      if (this.priceTrigger) this.priceTrigger(); // keep the button total in sync
    }

    /**
     * Include the gift-wrap price in the Add to Cart button total when the control is on.
     * The button price is rendered by the theme's <hdt-qty-price> (variant-price × qty).
     */
    async setupButtonPrice() {
      const qtyPrice = document.querySelector(`hdt-qty-price[form="${this.dataset.form}"]`);
      if (!qtyPrice) return; // "Show price on button" is off → nothing to extend

      const cents = parseFloat(this.dataset.giftWrapPrice) || 0;
      if (!cents) return;

      // Reuse the theme's own money formatter, loaded lazily so a failure here can never
      // break add-to-cart (which lives in this same file).
      let formatMoney;
      try {
        ({ formatMoney } = await import(this.dataset.utilitiesUrl));
      } catch (e) {
        return;
      }
      if (typeof formatMoney !== 'function') return;

      const input = this.input;
      const giftWrap = this;
      const qtyInput = document.querySelector(`[name="quantity"][form="${this.dataset.form}"]`);
      this.qtyInput = qtyInput;

      // Override the instance method. The theme's onVariantChanged calls this.updatPrice()
      // live, so variant changes pick this up automatically.
      qtyPrice.updatPrice = function () {
        const unit = parseFloat(this.getAttribute('variant-price')) || 0;
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        let total = unit * qty;
        if (input.checked && giftWrap.eligible) total += cents;
        const span = this.querySelector('.hdt-money');
        if (span) span.outerHTML = `<span class="hdt-money">${formatMoney(total)}</span>`;
        document.dispatchEvent(new CustomEvent('currency:update'));
      };

      // Quantity changes use a copy of the ORIGINAL method bound before this override, so add
      // our own listeners (registered after the theme's → run last → win).
      this.priceTrigger = () => qtyPrice.updatPrice();
      qtyInput?.addEventListener('change', this.priceTrigger);
      qtyInput?.addEventListener('input', this.priceTrigger);
      input.addEventListener('change', this.priceTrigger);

      // Reflect the initial (default-selected) state.
      qtyPrice.updatPrice();
    }

    onSubmit(event) {
      // Skip if the control is off, or the selected variant isn't gift-wrap eligible.
      if (!this.input.checked || !this.eligible) return;

      const form = event.currentTarget;
      const data = new FormData(form);
      if (!data.get('id')) return; // no resolvable main variant → let the theme handle it

      // Inject ONLY the gift wrap as an items[] entry. The main product is still added via the
      // form's own top-level id/quantity (the theme posts both in the same request), so adding
      // the main here too would double it.
      const injected = [];
      const addField = (name, value) => {
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.value = value;
        field.setAttribute('data-gift-wrap-injected', '');
        form.appendChild(field);
        injected.push(field);
      };
      addField('items[][id]', this.variantId);
      addField('items[][quantity]', 1);

      // The theme reads FormData synchronously in its own submit handler; clean up afterwards.
      setTimeout(() => {
        injected.forEach((field) => field.remove());
      }, 0);
    }

    /**
     * Dynamic checkout click. When the wrap is on, block Shopify's express handler, add the main
     * product + wrap to the cart in one /cart/add.js request, then redirect to checkout. The whole
     * cart goes to checkout (not just this product) — the only way to carry a second line item.
     */
    onBuyNow(event) {
      if (!this.input.checked || !this.eligible) return;

      const form = event.currentTarget.closest('form');
      if (!form) return;
      const data = new FormData(form);
      const mainId = data.get('id');
      if (!mainId) return; // no resolvable main variant → let Shopify handle it

      // Stop Shopify's express checkout BEFORE firing the async request.
      event.preventDefault();
      event.stopImmediatePropagation();

      // Rebuild the main line item from the form (carrying properties + selling plan), then the wrap.
      const main = { id: mainId, quantity: parseInt(data.get('quantity'), 10) || 1 };
      const properties = {};
      data.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) main.properties = properties;
      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) main.selling_plan = sellingPlan;

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [main, { id: this.variantId, quantity: 1 }] }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('cart/add failed');
          window.location.href = `${root}checkout`;
        })
        .catch(() => {
          // Fall back to the normal add-to-cart flow so the buyer isn't stranded.
          form.querySelector('[type="submit"][name="add"]')?.click();
        });
    }
  }

  customElements.define('hdt-gift-wrap', GiftWrap);
})();
